import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Printer, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, saveBlob, errMsg } from "@/lib/api";
import { money, fmtDate, todayISO, exportPdf } from "@/lib/format";
import { useApp } from "@/context/AppCtx";
import { PageHeader, Kpi, Empty, PrintPortal, StatusBadge, stockState } from "@/components/kit";
import { ChartTip } from "@/pages/Dashboard";
import { ReportSheet } from "@/components/ReportSheet";

const EXCELS = [
  ["ventas_dia", "Ventas por día"], ["ventas_mes", "Ventas por mes"], ["top", "Productos más vendidos"],
  ["inventario", "Estado del inventario"], ["cxc", "Cuentas por cobrar"],
];

const Section = ({ title, children, testId, className = "" }) => (
  <div className={`panel p-5 ${className}`} data-testid={testId}>
    <div className="label-cap mb-4">{title}</div>
    {children}
  </div>
);

export default function Reports() {
  const { settings } = useApp();
  const [range, setRange] = useState({ date_from: `${todayISO().slice(0, 4)}-01-01`, date_to: todayISO() });
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const sheet = useRef(null);

  useEffect(() => {
    api.get("/reports/summary", { params: range }).then((x) => setR(x.data));
  }, [range]);

  const excel = async (kind) => {
    try {
      const { data } = await api.get("/reports/excel", { params: { kind, ...range }, responseType: "blob" });
      saveBlob(data, `${kind}_${todayISO()}.xlsx`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pdf = async () => {
    setBusy(true);
    try {
      await exportPdf(sheet.current, `reporte_${todayISO()}.pdf`);
    } finally {
      setBusy(false);
    }
  };

  if (!r) return <div className="text-slate-400">Cargando…</div>;
  const inv = r.inventory;

  return (
    <div>
      <PageHeader title="Reportes" subtitle="Ventas, productos más vendidos, inventario y cuentas por cobrar">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="report-excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Exportar Excel</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#120E2E] border-[#271E56]">
            {EXCELS.map(([k, l]) => <DropdownMenuItem key={k} onClick={() => excel(k)} data-testid={`excel-${k}`}>{l}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" onClick={() => window.print()} data-testid="report-print-btn"><Printer className="w-4 h-4 mr-1" />Imprimir</Button>
        <Button className="btn-neon" onClick={pdf} disabled={busy} data-testid="report-pdf-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}PDF
        </Button>
      </PageHeader>

      <div className="panel p-4 flex flex-wrap items-end gap-3 mb-4">
        <div><div className="label-cap mb-1">Desde</div><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="report-date-from" /></div>
        <div><div className="label-cap mb-1">Hasta</div><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="report-date-to" /></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <Kpi testId="report-total-sales" label="Ventas del periodo" value={money(r.total_sales)} hint={`${r.invoice_count} facturas`} />
        <Kpi testId="report-receivables" label="Por cobrar" value={money(r.receivables.total)} accent="#F59E0B" />
        <Kpi testId="report-inv-cost" label="Inventario (costo)" value={money(inv.total_cost)} accent="#9D4EDD" />
        <Kpi testId="report-inv-sale" label="Inventario (venta)" value={money(inv.total_sale)} accent="#22D3EE" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Section title="Ventas por mes" testId="report-by-month">
          <div className="h-64">
            {r.by_month.length ? (
              <ResponsiveContainer>
                <BarChart data={r.by_month}>
                  <CartesianGrid stroke="#271E56" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} width={70} tickFormatter={(v) => `₡${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: "#1A143D" }} />
                  <Bar dataKey="total" fill="#9D4EDD" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="Sin ventas en el periodo" />}
          </div>
        </Section>
        <Section title="Ventas por día" testId="report-by-day">
          <div className="max-h-64 overflow-y-auto text-sm">
            {r.by_day.slice().reverse().map((d) => (
              <div key={d.date} className="flex justify-between py-1.5 border-t border-[#271E56]"><span className="text-slate-400">{fmtDate(d.date)}</span><span className="mono text-white">{money(d.total)}</span></div>
            ))}
            {!r.by_day.length && <Empty text="Sin ventas" />}
          </div>
        </Section>
        <Section title="Productos más vendidos" testId="report-top">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <tbody>
                {r.top_products.map((p) => (
                  <tr key={p.name} className="border-t border-[#271E56]"><td className="py-2 text-white">{p.name}</td><td className="mono text-slate-400 text-right">{p.qty} u.</td><td className="mono text-right text-[#00F0FF]">{money(p.total)}</td></tr>
                ))}
              </tbody>
            </table>
            {!r.top_products.length && <Empty text="Sin datos" />}
          </div>
        </Section>
        <Section title="Cuentas por cobrar" testId="report-cxc">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <tbody>
                {r.receivables.items.map((i) => (
                  <tr key={i.id} className="border-t border-[#271E56]"><td className="py-2 mono text-[#00F0FF]">{i.number}</td><td className="text-white">{i.client?.name}</td><td className="text-slate-400">{fmtDate(i.due_date)}</td><td className="mono text-right">{money(i.balance, i.currency)}{i.paid_amount > 0 && <div className="text-[11px] text-slate-500">de {money(i.total, i.currency)}</div>}</td></tr>
                ))}
              </tbody>
            </table>
            {!r.receivables.items.length && <Empty text="No hay cuentas pendientes" />}
          </div>
        </Section>
        <Section title="Estado del inventario" testId="report-inventory" className="xl:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <tbody>
                {inv.products.filter((p) => p.type !== "servicio").map((p) => (
                  <tr key={p.id} className="border-t border-[#271E56]">
                    <td className="py-2 mono text-slate-400">{p.code}</td><td className="text-white">{p.name}</td>
                    <td className="mono text-right">{p.stock} / {p.min_stock}</td><td className="mono text-right text-slate-400">{money(p.stock * p.cost_price)}</td>
                    <td className="text-right pl-3"><StatusBadge status={stockState(p)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      <div className="offscreen"><div ref={sheet}><ReportSheet r={r} range={range} s={settings} /></div></div>
      <PrintPortal><ReportSheet r={r} range={range} s={settings} /></PrintPortal>
    </div>
  );
}
